import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { RoamLoading } from '@/components/branding/RoamLoading';
import { useAuth } from '@/lib/auth/client-auth';
import { isLaunchSplashVisible } from '@/lib/pwa/app-launch';
import { tripLaunchLoadingLabel } from '@/lib/trip/tripLaunchLoading';

/**
 * Bounce an unauthenticated visitor to sign-in.
 *
 * The Next middleware used to do this before a byte was served. A static SPA
 * has no such hook, so the visitor now downloads the bundle first and is
 * redirected here instead. That is the accepted trade in DECISIONS #0023 and is
 * not a data-exposure change: Convex enforces authorization on every call, and
 * nothing protected is rendered before this runs.
 *
 * Applies to exactly the paths the middleware protected — `/trips*` and
 * `/settings*` — so that widening it stays a deliberate act.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { isLoaded, isSignedIn } = useAuth();
  const location = useLocation();

  // Redirecting before Clerk has resolved would bounce a signed-in visitor on
  // every cold load.
  if (!isLoaded) {
    if (isLaunchSplashVisible()) return null;
    return <RoamLoading label={tripLaunchLoadingLabel(location.pathname)} />;
  }
  if (!isSignedIn) {
    const next = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />;
  }
  return <>{children}</>;
}
